import React, { useState, useRef, useEffect } from 'react';
import { PlayingSide, PadelCategory, BeachTennisCategory, TennisCategory, Sport, Gender, Player, GameProposal, Availability } from '../types';
import Modal from './modals/Modal';
import CategoryTooltip from './tooltips/CategoryTooltip';
import PlayerHistory from './PlayerHistory';
import { Camera, AlertTriangle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { resizeImage } from '../utils/imageUtils';
import GroupsPanel from './groups/GroupsPanel';

interface EditProfileFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Player>) => void;
  currentUser: Player;
  games: GameProposal[];
  availabilities: Availability[];
  onViewGame: (game: GameProposal) => void;
  onRepublishGame?: (game: GameProposal) => void;
  onRepublishAvailability?: (availability: Availability, data: { duration: '7days' | '14days' }) => void;
  onRefreshAvailabilities?: () => void; // Add callback to refresh availabilities
  locations: Location[];
}

export default function EditProfileForm({ 
  isOpen, 
  onClose, 
  onSubmit,
  games,
  availabilities,
  onViewGame,
  onRepublishGame,
  onRepublishAvailability,
  onRefreshAvailabilities,
  locations
}: Omit<EditProfileFormProps, 'currentUser'>) {
  // Update this line to include deleteAccount
  const { user: currentUser, deleteAccount } = useAuth();
  
  // Update the state to include 'account' as a possible tab
  const [activeTab, setActiveTab] = useState<'profile' | 'history' | 'groups' | 'account'>('profile');
  
  // Add state for account deletion confirmation
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [accountDeleted, setAccountDeleted] = useState(false);
  
  // Add to state initialization
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    padelCategory: '',
    beachTennisCategory: '',
    tennisCategory: '', // Add this line
    playingSide: 'both' as PlayingSide,
    gender: 'male' as Gender,
    avatar: undefined as string | undefined,
    preferredSports: [] as Sport[],
    showOnlyGroupContent: false, // Add this new field
  });

  // Update useEffect to include the new field
  useEffect(() => {
    if (currentUser) {
      setFormData({
        name: currentUser.name || '',
        phone: currentUser.phone || '',
        email: currentUser.email || '',
        password: '',
        padelCategory: currentUser.padel_category || '',
        beachTennisCategory: currentUser.beach_tennis_category || '',
        tennisCategory: currentUser.tennis_category || '', // Add this line
        playingSide: currentUser.playing_side || 'both',
        gender: currentUser.gender || 'male',
        avatar: currentUser.avatar,
        preferredSports: currentUser.preferred_sports || ['padel', 'beach-tennis', 'tennis'],
        showOnlyGroupContent: currentUser.show_only_group_content || false,
      });
    }
  }, [currentUser]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const beachTennisCategories: BeachTennisCategory[] = [
    'INICIANTE',
    'CAT C',
    'CAT B',
    'CAT A',
    'PROFISSIONAL'
  ];

  const tennisCategories: TennisCategory[] = [
    '1.0', '1.5', '2.0', '2.5', '3.0', '3.5', '4.0', '4.5', '5.0', '5.5', '6.0', '6.5', '7.0'
  ];

  // Add helper functions here, before handleFileChange
  const shouldShowPadelCategory = () => {
    return formData.preferredSports.includes('padel');
  };

  const shouldShowBeachTennisCategory = () => {
    return formData.preferredSports.includes('beach-tennis');
  };

  const shouldShowTennisCategory = () => {
    return formData.preferredSports.includes('tennis');
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
  
    // Check file size (max 3MB)
    if (file.size > 3 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 3MB');
      return;
    }
  
    try {
      const resizedImage = await resizeImage(file);
      setFormData({ ...formData, avatar: resizedImage });
    } catch (err) {
      alert('Erro ao processar a imagem. Tente novamente.');
    }
  };

  const removePhoto = () => {
    setFormData({ ...formData, avatar: undefined });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Add state for sports selection error
  const [sportSelectionError, setSportSelectionError] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Modify the handleSubmit function
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSportSelectionError(false);
    setPasswordError('');
  
    // Validate sports selection
    if (formData.preferredSports.length === 0) {
      setSportSelectionError(true);
      return;
    }

    // Validate password if provided
    if (formData.password && formData.password.length < 6) {
      setPasswordError('A senha deve ter pelo menos 6 caracteres');
      return;
    }
  
    // Use both camelCase and snake_case to ensure compatibility
    const updatedData: Partial<Player> = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      // Include both formats to ensure one works
      padel_category: formData.padelCategory || null,
      padelCategory: formData.padelCategory || null,
      beach_tennis_category: formData.beachTennisCategory || null,
      beachTennisCategory: formData.beachTennisCategory || null,
      tennis_category: formData.tennisCategory || null,
      tennisCategory: formData.tennisCategory || null,
      playing_side: formData.playingSide,
      playingSide: formData.playingSide,
      gender: formData.gender,
      avatar: formData.avatar,
      preferred_sports: formData.preferredSports,
      show_only_group_content: formData.showOnlyGroupContent,
      ...(formData.password ? { password: formData.password } : {})
    };
  
    onSubmit(updatedData);
  };

  // Add this check before the return statement
  if (!currentUser) {
    return null;
  }

  // Add function to handle account deletion
  const handleDeleteAccount = () => {
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteAccount = async () => {
    try {
      // Show loading state or disable button here if needed
      
      // Call the deleteAccount function from useAuth
      await deleteAccount();
      
      // Show success message
      setAccountDeleted(true);
      setShowDeleteConfirmation(false);
      
      // Close the modal after a short delay
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Erro ao excluir conta. Tente novamente.');
      setShowDeleteConfirmation(false);
    }
  };

  const cancelDeleteAccount = () => {
    setShowDeleteConfirmation(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Minha Conta"
    >
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6">
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'profile'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Perfil
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Histórico
          </button>
          <button
            onClick={() => setActiveTab('groups')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'groups'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Grupos
          </button>
          <button
            onClick={() => setActiveTab('account')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'account'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Conta
          </button>
        </nav>
      </div>

      {activeTab === 'profile' ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col items-center">
            <div className="relative">
              <div className={`w-24 h-24 rounded-full overflow-hidden border-2 border-gray-200 ${
                formData.avatar ? 'bg-gray-100' : 'bg-gray-50'
              }`}>
                {formData.avatar ? (
                  <img
                    src={formData.avatar}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <Camera className="w-8 h-8" />
                  </div>
                )}
              </div>
              
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              
              <div className="mt-2 flex flex-col items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {formData.avatar ? 'Alterar foto' : 'Adicionar foto'}
                </button>
                
                {formData.avatar && (
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Remover foto
                  </button>
                )}
              </div>
            </div>
          </div>
          

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Modalidades que quero jogar
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.preferredSports.includes('padel')}
                  onChange={(e) => {
                    const newSports = e.target.checked
                      ? [...formData.preferredSports, 'padel']
                      : formData.preferredSports.filter(s => s !== 'padel');
                    
                    // Clear padel category if padel is unchecked
                    const updatedFormData = { 
                      ...formData, 
                      preferredSports: newSports,
                      ...(e.target.checked ? {} : { padelCategory: '' })
                    };
                    
                    setFormData(updatedFormData);
                    setSportSelectionError(false);
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">Padel</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.preferredSports.includes('beach-tennis')}
                  onChange={(e) => {
                    const newSports = e.target.checked
                      ? [...formData.preferredSports, 'beach-tennis']
                      : formData.preferredSports.filter(s => s !== 'beach-tennis');
                    
                    // Clear beach tennis category if beach tennis is unchecked
                    const updatedFormData = { 
                      ...formData, 
                      preferredSports: newSports,
                      ...(e.target.checked ? {} : { beachTennisCategory: '' })
                    };
                    
                    setFormData(updatedFormData);
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">Beach Tennis</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.preferredSports.includes('tennis')}
                  onChange={(e) => {
                    const newSports = e.target.checked
                      ? [...formData.preferredSports, 'tennis']
                      : formData.preferredSports.filter(s => s !== 'tennis');
                    
                    // Clear tennis category if tennis is unchecked
                    const updatedFormData = { 
                      ...formData, 
                      preferredSports: newSports,
                      ...(e.target.checked ? {} : { tennisCategory: '' })
                    };
                    
                    setFormData(updatedFormData);
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">Tênis</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.preferredSports.length === 3} // Update to check for all three sports
                  onChange={(e) => {
                    const allSports = ['padel', 'beach-tennis', 'tennis'] as Sport[];
                    const updatedFormData = {
                      ...formData,
                      preferredSports: e.target.checked ? allSports : [],
                      // Clear all categories if unchecking "Todas"
                      ...(e.target.checked ? {} : { 
                        padelCategory: '', 
                        beachTennisCategory: '', 
                        tennisCategory: '' 
                      })
                    };
                    setFormData(updatedFormData);
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">Todas</span>
              </label>
            </div>
            {sportSelectionError && (
              <p className="mt-2 text-sm text-red-600">
                Selecione pelo menos uma modalidade
              </p>
            )}
          </div>

          {/* Add the new group content filter option */}
          <div className="flex items-center gap-2 mt-2">
            <input
              type="checkbox"
              id="showOnlyGroupContent"
              checked={formData.showOnlyGroupContent}
              onChange={(e) => setFormData({ ...formData, showOnlyGroupContent: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="showOnlyGroupContent" className="text-sm text-gray-700">
              Mostrar apenas jogos e disponibilidades dos meus grupos
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome Completo ou apelido
            </label>
            <input
              type="text"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nova Senha (deixe em branco para manter a atual)
            </label>
            <input
              type="password"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.password}
              onChange={(e) => {
                setFormData({ ...formData, password: e.target.value });
                setPasswordError(''); // Clear error when user types
              }}
              placeholder="Digite apenas se quiser alterar a senha"
            />
            {passwordError && (
              <p className="mt-2 text-sm text-red-600">
                {passwordError}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefone
            </label>
            <input
              type="tel"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>

          <div className="space-y-6">
            {/* Add this new section for playing side preference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Lado preferencial
              </label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="playingSide"
                    value="both"
                    checked={formData.playingSide === 'both'}
                    onChange={(e) => setFormData({ ...formData, playingSide: e.target.value as PlayingSide })}
                    className="w-4 h-4 text-blue-600 border-gray-300"
                  />
                  <span className="text-gray-700">Ambos</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="playingSide"
                    value="left"
                    checked={formData.playingSide === 'left'}
                    onChange={(e) => setFormData({ ...formData, playingSide: e.target.value as PlayingSide })}
                    className="w-4 h-4 text-blue-600 border-gray-300"
                  />
                  <span className="text-gray-700">Esquerdo</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="playingSide"
                    value="right"
                    checked={formData.playingSide === 'right'}
                    onChange={(e) => setFormData({ ...formData, playingSide: e.target.value as PlayingSide })}
                    className="w-4 h-4 text-blue-600 border-gray-300"
                  />
                  <span className="text-gray-700">Direito</span>
                </label>
              </div>
            </div>
          
            {/* Existing gender selection section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Gênero
              </label>
            <select
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.gender}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value as Gender })}
            >
              <option value="male">Masculino</option>
              <option value="female">Feminino</option>
            </select>
          </div>

          {shouldShowPadelCategory() && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Categoria Padel (Opcional)
                </label>
                <CategoryTooltip />
              </div>
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.padelCategory || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  padelCategory: e.target.value ? e.target.value as PadelCategory : undefined
                })}
              >
                <option value="">Selecione a categoria (opcional)</option>
                <option value="CAT 1">CAT 1</option>
                <option value="CAT 2">CAT 2</option>
                <option value="CAT 3">CAT 3</option>
                <option value="CAT 4">CAT 4</option>
                <option value="CAT 5">CAT 5</option>
                <option value="CAT 6">CAT 6</option>
              </select>
            </div>
          )}
          
          {shouldShowBeachTennisCategory() && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoria Beach Tennis (Opcional)
              </label>
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.beachTennisCategory || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  beachTennisCategory: e.target.value ? e.target.value as BeachTennisCategory : undefined
                })}
              >
                <option value="">Selecione a categoria (opcional)</option>
                {beachTennisCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Add after beach tennis category selection */}
          {shouldShowTennisCategory() && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Categoria Tênis (Opcional)
                </label>
                <CategoryTooltip />
              </div>
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.tennisCategory || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  tennisCategory: e.target.value ? e.target.value as TennisCategory : undefined
                })}
              >
                <option value="">Selecione a categoria (opcional)</option>
                {tennisCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          )}

          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Salvar Alterações
            </button>
          </div>
        </form>
      ) : activeTab === 'history' ? (
        <PlayerHistory
          player={currentUser}
          games={games}
          availabilities={availabilities}
          onViewGame={onViewGame}
          onRepublishGame={onRepublishGame}
          onRepublishAvailability={onRepublishAvailability}
          locations={locations}
        />
      ) : activeTab === 'groups' ? (
        <GroupsPanel 
          currentUser={currentUser} 
          onGroupJoined={onRefreshAvailabilities}
          onDataRefresh={onRefreshAvailabilities}
        />
      ) : activeTab === 'account' ? (
        <div className="space-y-6">
          {accountDeleted ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900">Conta excluída com sucesso</h3>
                <p className="mt-2 text-gray-600">
                  Sua conta foi excluída permanentemente. Todos os seus dados foram removidos dos nossos servidores.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-4 px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Gerenciar Conta</h3>
                <p className="text-gray-600 text-sm">
                  Aqui você pode gerenciar configurações avançadas da sua conta.
                </p>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h4 className="text-lg font-medium text-red-800 mb-2">Atenção</h4>
                <p className="text-gray-700 mb-4">
                  Excluir sua conta é uma ação permanente e não pode ser desfeita. 
                  Todos os seus dados serão removidos permanentemente.
                </p>
                
                {showDeleteConfirmation ? (
                  <div className="bg-white border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                      </div>
                      <div>
                        <h5 className="text-sm font-medium text-gray-900">Tem certeza que deseja excluir sua conta?</h5>
                        <p className="text-xs text-gray-600 mt-1">
                          Esta ação não pode ser desfeita. Todos os seus dados, incluindo jogos, disponibilidades e informações pessoais serão excluídos permanentemente.
                        </p>
                        <div className="mt-4 flex gap-3">
                          <button
                            type="button"
                            onClick={confirmDeleteAccount}
                            className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                          >
                            Sim, excluir minha conta
                          </button>
                          <button
                            type="button"
                            onClick={cancelDeleteAccount}
                            className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleDeleteAccount}
                    className="px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    Excluir minha conta
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      ) : null}
    </Modal>
  );
}